import { useState, useEffect } from "react";

interface SimulationMetricsData {
  // Base
  max_base_deficit: number;
  total_base_deficit: number;
  total_base_revenue: number;
  total_base_cost: number;

  // Optimal
  max_optimal_deficit: number;
  total_optimal_deficit: number;
  total_optimal_revenue: number;
  total_optimal_cost: number;

  // Simulated
  max_simulated_deficit: number;
  total_simulated_deficit: number;
  total_simulated_revenue: number;
  total_simulated_cost: number;
}

interface MetricCard {
  label: string;
  value: string;
  unit: string;
  sublabel:string;
}

// Skeleton Loader Component
const SkeletonLoader = () => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
        <div
          key={item}
          className={`${
            item >= 5
              ? "bg-[#EFFCFF] border-[#34656D]"
              : "bg-[#FFFAF6] border-[#F1935C]"
          } rounded-3xl shadow-md border px-4 py-4 animate-pulse`}
        >
          <div className="space-y-2">
            {/* Label Skeleton */}
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            
            {/* Value Skeleton */}
            <div className="flex items-baseline gap-2">
              <div className="h-8 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function GnaMetricsCostAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  loading,
  data,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  loading: boolean;
  data: SimulationMetricsData[];
}) {
  const [capacityData, setCapacityData] = useState<MetricCard[]>([]);

  // Format number with commas
  const formatNumber = (num: number): string => {
    return num.toLocaleString("en-IN");
  };

  useEffect(() => {
    if (data && data.length > 0) {
      // Assuming you want to use the first item or aggregate data
      const apiData = data[0];

      const formattedData: MetricCard[] = [
        {
          label: "Total Base Revenue",
          value: formatNumber(apiData.total_base_revenue),
          sublabel: "Revenue from Selling Surplus in DAM",
          unit: " Lakh ₹",
        },
        {
          label: "Total Base Cost",
          value: formatNumber(apiData.total_base_cost),
          sublabel: "Cost of Purchasing Deficit in DAM",
          unit: " Lakh ₹",
        },
        {
          label: "Total Optimal Revenue",
          value: formatNumber(apiData.total_optimal_revenue),
          sublabel: "Revenue from Selling Surplus in DAM",
          unit: " Lakh ₹",
        },
        {
          label: "Total Optimal Cost",
          value: formatNumber(apiData.total_optimal_cost),
          sublabel: "Cost of Purchasing Deficit in DAM",
          unit: " Lakh ₹",
        },
      ];

      setCapacityData(formattedData);
    }
  }, [data, refreshKey]);

  return (
    <>
      {loading ? (
        <div className="w-full">
          <div className="flex flex-nowrap items-center justify-around py-2 px-2">
            <div className="w-full h-full">
              <SkeletonLoader />
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full">
          <div className="flex flex-nowrap items-center justify-around py-2 px-2">
            <div className="w-full h-full">
              {capacityData.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {capacityData.map((item, index) => (
                    <div
                      key={index}
                      className={`${
                        index >= 2
                          ? "bg-[#FFFAF6] border-[#F1935C]"
                          : "bg-[#ECF3FF66] border-[#617499]"
                      } rounded-3xl border px-4 py-4 transition-all hover:shadow-lg`}
                    >
                      <div className="space-y-2">
                        <h3
                          className={`text-base font-medium ${
                            index >= 2 ? "text-[#F1935C]" : "text-[#819ACC]"
                          }`}
                        >
                          {item.label}
                        </h3>
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span
                            className={`text-xl text-[#2B3674] ${
                              index >= 4 ? "font-normal" : "font-medium"
                            }`}
                          >
                            {item.value} {item.unit}
                          </span>
                        </div>
                        <h3
                          className={`text-sm font-medium ${
                            index >= 2 ? "text-[#F1935C]" : "text-[#819ACC]"
                          }`}
                        >
                          {item.sublabel}
                        </h3>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No Data Available...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}